import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import CallStatusBadge from './CallStatusBadge';
import { cn } from '../../../utils/cn';
import { formatCandidateDisplayValue } from '../../../lib/utils/formatCandidateField';
import { formatCallDuration } from '../../../lib/utils/formatCallDuration';
import { getAIReturnPathFromLocation } from '../../../lib/utils/aiCallsRoutes';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const AICallsTableRow = ({ call }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const candidate = call.candidate;
  const fullName = candidate?.full_name || t('ai.calls.unknownCandidate');

  const formatCallDateTime = (date) => (
    date ? format(new Date(date), 'dd-MM-yyyy HH:mm') : t('common.na')
  );

  const handleViewProfile = () => {
    if (!call.candidateId) return;
    navigate(`/ai-assistant/candidate/${call.candidateId}`, {
      state: { returnTo: getAIReturnPathFromLocation(location) },
    });
  };

  return (
    <tr
      className="group border-b border-border cursor-pointer"
      onClick={handleViewProfile}
    >
      <td className="px-4 py-4 transition-colors group-hover:bg-muted w-[20%] min-w-[200px]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
            {candidate?.avatar ? (
              <Image
                src={candidate.avatar}
                alt={t('ai.calls.profileAlt', { name: fullName })}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                {getInitials(fullName)}
              </div>
            )}
          </div>
          <div className="min-w-0 max-w-[420px]">
            <p className="font-medium text-foreground truncate">{fullName}</p>
            <p className="text-sm text-muted-foreground truncate">
              {formatCandidateDisplayValue(candidate?.current_position || t('common.notSpecified'))}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap transition-colors group-hover:bg-muted w-[170px]">
        <p className="text-sm text-muted-foreground">
          {formatCallDateTime(call.created_at)}
        </p>
      </td>
      <td className="px-4 py-4 whitespace-nowrap transition-colors group-hover:bg-muted w-[110px]">
        <p className="text-sm text-muted-foreground tabular-nums">
          {formatCallDuration(call.callDuration, t('common.emDash'))}
        </p>
      </td>
      <td className="px-4 py-4 whitespace-nowrap transition-colors group-hover:bg-muted w-[180px]">
        <CallStatusBadge status={call.status} />
      </td>
      <td className="px-4 py-4 transition-colors group-hover:bg-muted max-w-0 w-full">
        <p className="text-sm text-foreground truncate" title={call.summary}>
          {call.summary}
        </p>
      </td>
    </tr>
  );
};

const AICallsTable = ({
  calls,
  className,
  showSectionHeader = false,
  sectionTitle,
  viewAllLabel,
  viewAllPath = '/ai-assistant/calls',
  emptyMessage,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resolvedSectionTitle = sectionTitle ?? t('ai.calls.recentCalls');
  const resolvedViewAllLabel = viewAllLabel ?? t('ai.calls.viewAllCalls');
  const resolvedEmptyMessage = emptyMessage ?? t('ai.calls.emptyTable');

  const tableHead = (
    <thead className="bg-muted/50 border-b border-border">
      <tr>
        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-[20%] min-w-[200px]">
          {t('ai.calls.tableCandidate')}
        </th>
        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap w-[170px]">
          {t('ai.calls.tableDateTime')}
        </th>
        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap w-[110px]">
          {t('ai.calls.tableDuration')}
        </th>
        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap w-[180px]">
          {t('ai.calls.tableStatus')}
        </th>
        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {t('ai.calls.tableSummary')}
        </th>
      </tr>
    </thead>
  );

  const tableBody = calls?.length ? (
    <tbody className="divide-y divide-border">
      {calls.map((call) => (
        <AICallsTableRow key={call.id} call={call} />
      ))}
    </tbody>
  ) : null;

  const emptyState = (
    <div className="p-8 text-center">
      <p className="text-muted-foreground">{resolvedEmptyMessage}</p>
    </div>
  );

  const tableContent = calls?.length ? (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed">
        {tableHead}
        {tableBody}
      </table>
    </div>
  ) : (
    emptyState
  );

  if (showSectionHeader) {
    return (
      <div className={cn('bg-card rounded-lg border border-border shadow-elevation-sm', className)}>
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">{resolvedSectionTitle}</h2>
          <Button
            variant="outline"
            onClick={() => navigate(viewAllPath)}
            iconName="ArrowRight"
            iconPosition="right"
          >
            {resolvedViewAllLabel}
          </Button>
        </div>
        {tableContent}
      </div>
    );
  }

  if (!calls?.length) {
    return (
      <div className={cn('bg-card border border-border rounded-lg p-8 text-center', className)}>
        <p className="text-muted-foreground">{resolvedEmptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn('bg-card border border-border rounded-lg overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          {tableHead}
          {tableBody}
        </table>
      </div>
    </div>
  );
};

export default AICallsTable;
